<script setup lang="ts">
  import { cn } from "@/lib/utils"
  import {
    PinInputInput,
    useForwardProps,
    type PinInputInputProps,
  } from "radix-vue"
  import { computed, type HTMLAttributes } from "vue"

  const props = defineProps<
    PinInputInputProps & { class?: HTMLAttributes["class"] }
  >()

  const delegatedProps = computed(() => {
    const { class: _, ...delegated } = props
    return delegated
  })

  const forwardedProps = useForwardProps(delegatedProps)

  const onFocus = (e: FocusEvent) => {
    // select the input value when focused so that we can guarantee that the
    // cursor will always be at the end of the text. If the user focuses
    // the input by clicking just to the left of the text, their cursor will be
    // at the beginning and the underlying component will fail to replace it the
    // existing text when they type a new number.
    const target: null | HTMLInputElement = e.target as HTMLInputElement
    const value = target?.value
    if (value && value.length > 0) {
      target?.setSelectionRange(0, value.length)
    }
  }
</script>

<template>
  <PinInputInput
    v-bind="forwardedProps"
    autocomplete="off"
    :class="
      cn(
        'text-center text-sm text-secondary-500',
        'h-[52px] min-w-0 max-w-10',
        'border-y border-r border-secondary-300 ring-inset transition-all first-of-type:rounded-l-lg first-of-type:border-l last-of-type:rounded-r-lg',
        'hover:outline-none hover:ring-1 hover:ring-secondary-400 focus:relative focus:z-10 focus:outline-none focus:ring-2 focus:ring-primary-400',
        'disabled:pointer-events-none disabled:border-secondary-500 disabled:bg-secondary-300 disabled:text-secondary-500 disabled:placeholder-secondary-500',
        props.class,
      )
    "
    @focus="onFocus"
  />
</template>
